/**
 * AI assistant panel state.
 *
 * Messages are the canonical rows from the API, keyed by sequence. Live
 * frames from the notifications socket (type "ai_chat") update a streaming
 * buffer and an activity list; when a turn completes we re-fetch the rows
 * after the last known sequence so the transcript always converges on what
 * the server persisted, whether or not the socket delivered every frame.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as aiChatAPI from '@/api/modules/aiChat'
import { frameReceived } from './frames'
import { invalidateAfterConfirm } from './invalidateAfterConfirm'
import { selectSetupHelpAvailable, togglePanel } from './panelVisibility'
import { AI_PERSONA_VALUES, persistPersona, readPersistedPersona } from './personas'
import { LOGOUT_ACTION_TYPES } from '@/features/auth/logoutActionTypes'
import { TENANT_CONTEXT_CHANGED, TENANT_SWITCH_FULFILLED } from '@/features/auth/tenantSwitchActionType'

const errMsg = (error, fallback) => error.apiError?.message || error.response?.data?.message || error.message || fallback
const errCode = (error) => error.apiError?.code || error.response?.data?.error || null
const errStatus = (error) => error.apiError?.status ?? error.response?.status ?? null
/** JSON:API `errors[0].meta` (e.g. `unmapped_campuses` on `campus_map_incomplete`), or null. */
const errMeta = (error) => error.apiError?.errors?.[0]?.meta || null

/**
 * Confirm refusals a draft card explains itself (A11 `rock_metric` applier),
 * instead of the panel's generic error banner. The draft stays pending after
 * each of these, so the card keeps its Confirm button for a second try once
 * the viewer has fixed what it names.
 */
export const CARD_CONFIRM_ERROR_CODES = ['rock_writes_disabled', 'campus_map_incomplete', 'rock_write_unavailable', 'template_unknown']

/** Free setup help ran out on a plan without the assistant (a send, a session create, or the worker). */
const SETUP_CHAT_ENDED = 'setup_chat_ended'

/** A poll failing with one of these will never succeed: stop at once. */
const TERMINAL_POLL_STATUSES = [403, 404]
/** Any other poll failure backs off (AiChatPanel) and gives up after this many in a row. */
export const MAX_POLL_FAILURES = 6
const POLL_GAVE_UP_MESSAGE = 'Lost touch with the assistant. Open the conversation again to check for the reply.'
const POLL_NOT_FOUND_MESSAGE = 'This conversation is no longer available.'

export const loadStatus = createAsyncThunk('aiChat/loadStatus', async (_, { rejectWithValue }) => {
  try {
    return await aiChatAPI.fetchStatus()
  } catch (error) {
    return rejectWithValue({ message: errMsg(error, 'Assistant unavailable'), code: errCode(error) })
  }
})

export const loadStarters = createAsyncThunk('aiChat/loadStarters', async (params, { rejectWithValue }) => {
  try {
    return await aiChatAPI.fetchStarters(params)
  } catch (error) {
    return rejectWithValue(errMsg(error, 'Could not load suggestions'))
  }
})

/**
 * Page-aware suggestion chips (A8). Failure is silent — an endpoint that
 * 404s because A8 hasn't shipped yet, or any other error, just means no
 * suggestions this time, not a panel-blocking error.
 */
export const loadSuggestions = createAsyncThunk('aiChat/loadSuggestions', async (params, { rejectWithValue }) => {
  try {
    return await aiChatAPI.fetchSuggestions(params)
  } catch (error) {
    return rejectWithValue(errMsg(error, ''))
  }
})

export const loadSessions = createAsyncThunk('aiChat/loadSessions', async (_, { rejectWithValue }) => {
  try {
    return await aiChatAPI.listSessions()
  } catch (error) {
    return rejectWithValue(errMsg(error, 'Could not load conversations'))
  }
})

/**
 * `starting` (set on pending) is the guard against a double submit creating
 * two sessions: the panel's submit awaits this before `sending` is ever set,
 * so without it a second click in that window starts a second session and a
 * second turn. The `condition` makes the guard hold even for a caller whose
 * closure still reads the pre-click state. `purpose: 'onboarding'` asks for
 * setup help; the session's own `purpose` says what the backend granted.
 */
export const startSession = createAsyncThunk('aiChat/startSession', async ({ context, purpose } = {}, { rejectWithValue }) => {
  try {
    return await aiChatAPI.createSession(context || {}, purpose)
  } catch (error) {
    return rejectWithValue({ message: errMsg(error, 'Could not start a conversation'), code: errCode(error) })
  }
}, { condition: (_, { getState }) => !getState().aiChat?.starting })

export const openSession = createAsyncThunk('aiChat/openSession', async (sessionId, { rejectWithValue }) => {
  try {
    const [detail, page] = await Promise.all([aiChatAPI.fetchSession(sessionId), aiChatAPI.fetchMessages(sessionId)])
    return { session: detail.session, inFlightTurn: detail.in_flight_turn, messages: page.messages }
  } catch (error) {
    return rejectWithValue(errMsg(error, 'Could not open the conversation'))
  }
})

/**
 * `turnIdAtDispatch` (the turn we were tracking when the request went out,
 * or null) anchors the response instead of a wall-clock timestamp (FE-A9,
 * replacing B5's `issuedAt`/`startedAt` comparison, which could skew
 * between the client's clock and a server `created_at`). A per-turn frame
 * `seq` is what makes a turn's identity trustworthy for this: the backend
 * scopes its counter to exactly one turn, so once that turn is no longer
 * the one we're tracking, anything this response says about turn state is
 * definitionally stale, no clock needed.
 *
 * `last_seq` (fix round 1) rides along in `page` unchanged — the backend's
 * own frame high-water mark for the in-flight turn as of this snapshot
 * (`GET .../messages/`'s top-level field), a second, same-turn guard: two
 * polls for the SAME turn (so the id anchor above can't tell them apart)
 * can still resolve out of order, and applying the older one's rows after a
 * newer one (or a live frame) already landed would regress what's shown.
 */
export const loadMessages = createAsyncThunk('aiChat/loadMessages', async ({ sessionId, after }, { getState, rejectWithValue }) => {
  try {
    const page = await aiChatAPI.fetchMessages(sessionId, after)
    const turnIdAtDispatch = getState().aiChat.inFlightTurn?.id ?? null
    return { sessionId, turnIdAtDispatch, ...page }
  } catch (error) {
    return rejectWithValue({
      message: errMsg(error, 'Could not refresh the conversation'),
      code: errCode(error),
      status: errStatus(error),
    })
  }
})

export const sendMessage = createAsyncThunk(
  'aiChat/sendMessage',
  async ({ sessionId, text, context }, { dispatch, getState, rejectWithValue }) => {
    let result
    try {
      result = await aiChatAPI.sendMessage(sessionId, text, context)
    } catch (error) {
      return rejectWithValue({ message: errMsg(error, 'Could not send your message'), code: errCode(error) })
    }
    // A setup conversation billed to the plan while the status still offers
    // free setup help: the help just ran out. Re-read the status so the panel
    // and the help buttons go by the plan again.
    const state = getState()
    if (result.billing === 'plan' && state.aiChat.sessionPurpose === 'onboarding' && selectSetupHelpAvailable(state)) {
      dispatch(loadStatus())
    }
    return result
  }
)

export const cancelTurn = createAsyncThunk('aiChat/cancelTurn', async (turnId, { rejectWithValue }) => {
  try {
    return await aiChatAPI.cancelTurn(turnId)
  } catch (error) {
    return rejectWithValue(errMsg(error, 'Could not stop the reply'))
  }
})

export const confirmDraft = createAsyncThunk('aiChat/confirmDraft', async (draftId, { rejectWithValue }) => {
  try {
    const result = await aiChatAPI.confirmDraft(draftId)
    invalidateAfterConfirm(result)
    return { draftId, ...result }
  } catch (error) {
    return rejectWithValue({ draftId, message: errMsg(error, 'Could not save'), code: errCode(error), meta: errMeta(error) })
  }
})

export const rejectDraft = createAsyncThunk('aiChat/rejectDraft', async ({ draftId, reason }, { rejectWithValue }) => {
  try {
    return { draftId, ...(await aiChatAPI.rejectDraft(draftId, reason)) }
  } catch (error) {
    return rejectWithValue({ draftId, message: errMsg(error, 'Could not dismiss the draft') })
  }
})

export const sendFeedback = createAsyncThunk('aiChat/sendFeedback', async ({ messageId, ...payload }, { rejectWithValue }) => {
  try {
    await aiChatAPI.sendFeedback(messageId, payload)
    return { messageId, is_positive: payload.is_positive }
  } catch (error) {
    return rejectWithValue(errMsg(error, 'Could not record feedback'))
  }
})

const initialState = {
  panelOpen: false,
  // Per-viewer, not per-tenant or per-session — read from localStorage
  // (readPersistedPersona is itself try/catch-guarded). A logout or tenant
  // switch re-reads it (freshInitialState below) rather than reverting to
  // this boot-time snapshot.
  persona: readPersistedPersona(),
  // The composer's unsent text, lifted out of AiChatPanel's own component
  // state (fix round 1, B4): the panel returns null while closed rather
  // than unmounting, so this survived a close/reopen either way, but living
  // in the slice means the ⌘J shortcut - or anything else that toggles the
  // panel - can never lose it, and it reads back the same way sessionId etc.
  // do. Named `composerDraft`, not `draft`, so it can't be confused with the
  // unrelated "AI-proposed draft" concept (`draftId`/`draftBusy`/
  // `resolvedDrafts`/`savedDrafts` below - a report/dashboard/metric the
  // assistant proposes, not text the viewer is typing).
  composerDraft: '',
  // A "Get help with this step" button's `{topic, step}` (askForSetupHelp),
  // sent as `setup_topic`/`setup_step` until the next send goes through.
  setupRequest: null,
  sessionPurpose: null, // the open session's `purpose` ('chat' | 'onboarding'), null without one
  billing: null, // the last send's `billing`: 'comped' (free setup help) | 'plan'
  status: null, // { enabled, role, can_propose, read_only, budget, setup_chat, ... }
  statusError: null,
  starters: [],
  suggestions: [], // A8 chips: [{label, prompt, metric_id, direction}]; A11 onboarding chips: [{label, prompt, action, template_key?}]
  // requestId of the latest loadSuggestions; any other response is for a page the viewer already left.
  suggestionsRequestId: null,
  sessions: [],
  sessionId: null,
  sessionTitle: '',
  messages: [], // canonical rows sorted by sequence
  inFlightTurn: null, // { id, status, startedAt }
  // Sequence of the user row that opened the in-flight turn. Polls ask for
  // everything after it, not after the last known row: the backend creates
  // each assistant row empty and fills its text in when the round ends, so
  // a poll keyed on the last sequence would never see the answer arrive.
  turnAnchor: null,
  syncFrom: null, // where the post-turn converge fetch starts (the anchor of the turn that just ended)
  streaming: null, // { messageId, text }
  // The highest frame `seq` applied so far, per turn (FE-A9; fix round 1:
  // was a single mutable slot, which a confirm/reject note frame for a
  // DIFFERENT, earlier turn than the active one would blow away, letting a
  // redelivered frame of the active turn slip past dedup). Keyed by turn id
  // (or `session-<id>` for a turn-less frame, matching the backend's own
  // fallback key) so one turn's counter is never clobbered by another's.
  frameSeqByTurn: {},
  activities: [], // live tool timeline for the in-flight turn: [{ callId, tool, label, done, success, summary, startedAt, finishedAt }]
  turnMeta: {}, // turnId -> { startedAt, endedAt, stopped, activities } for turns seen live this session
  stoppingTurnId: null, // cancel requested, waiting for the worker to stop
  sending: false,
  starting: false, // startSession in flight (see its condition)
  pendingUserText: '', // the question being sent, shown before the server echoes it back
  pendingStartedAt: null,
  // user message id -> the list key its optimistic exchange rendered under,
  // so the echoed row keeps the same element (no remount, no replayed entrance)
  exchangeKeys: {},
  cancelling: false,
  error: null,
  errorCode: null, // backend code behind `error` when it has one (ai_chat_disabled, not_found, ...)
  pollFailures: 0, // loadMessages failures in a row; drives the panel's poll back-off
  draftBusy: {}, // draftId -> true while confirming/rejecting
  resolvedDrafts: {}, // draftId -> 'approved' | 'rejected'
  // draftId -> { kind, id, name, url_path, status?, setup_run_id?, connection_id? } from the confirm response,
  // available before the "saved" message row syncs in. `status: 'accepted'` (202) means a background job was
  // queued (a Rock setup run) rather than an object saved; the card polls `setup_run_id`.
  savedDrafts: {},
  draftErrors: {}, // draftId -> { code, message, meta } for a CARD_CONFIRM_ERROR_CODES refusal; cleared on the next try
  feedback: {}, // messageId -> true/false
  needsSync: false, // set by turn_completed; the panel re-fetches rows
}

/**
 * Merges rows by sequence. A field the incoming row leaves undefined never
 * blanks one already known (a frame-built row has no created_at; the polled
 * row does), and an empty incoming `content` never wipes text that already
 * arrived (a frame can race a poll that read the row before it was filled).
 */
function upsertMessages(state, rows) {
  const bySeq = new Map(state.messages.map((m) => [m.sequence, m]))
  for (const row of rows) {
    if (row?.sequence == null) continue
    const prev = bySeq.get(row.sequence) || {}
    const next = { ...prev }
    for (const [key, value] of Object.entries(row)) {
      if (value === undefined) continue
      if (key === 'content' && !value && prev.content) continue
      if (key === 'ui_payload' && value == null && prev.ui_payload) continue
      next[key] = value
    }
    bySeq.set(row.sequence, next)
  }
  state.messages = Array.from(bySeq.values()).sort((a, b) => a.sequence - b.sequence)
}

/** @param {string} [endedTurnId] - delete just this turn's seq entry; omitted (a full session reset) clears all of them. */
function clearTurnState(state, endedTurnId) {
  state.inFlightTurn = null
  state.turnAnchor = null
  state.streaming = null
  state.activities = []
  state.stoppingTurnId = null
  if (endedTurnId) delete state.frameSeqByTurn[endedTurnId]
  else state.frameSeqByTurn = {}
}

/** A turn ended (frame, poll, or cancel): keep its timing and timeline, then converge on the persisted rows. */
function endTurn(state, { status } = {}) {
  const turn = state.inFlightTurn
  if (!turn) return
  const stopped = status === 'cancelled' || state.stoppingTurnId === turn.id
  state.turnMeta[turn.id] = {
    ...(state.turnMeta[turn.id] || {}),
    startedAt: turn.startedAt,
    endedAt: Date.now(),
    stopped,
    activities: state.activities,
  }
  state.syncFrom = state.turnAnchor
  clearTurnState(state, turn.id)
  state.needsSync = true
}

/**
 * A turn we can no longer follow (the poll is forbidden, gone, or kept
 * failing): end it without the post-turn converge fetch, which would only
 * fail the same way, and say why.
 */
function abandonTurn(state, message, code) {
  endTurn(state)
  state.needsSync = false
  state.syncFrom = null
  state.pollFailures = 0
  state.error = message
  state.errorCode = code
}

/**
 * Mirror a worker-side refusal (`error` frame `code`) onto `status` in the
 * shape GET /api/ai/status/ uses: `enabled: false` plus `reason` (the text
 * for not-enabled, the literal `budget_exceeded` for the allowance).
 */
function applyBlockingErrorCode(state, code, message) {
  if (code === 'budget_exceeded') {
    state.status = { ...(state.status || {}), enabled: false, reason: 'budget_exceeded', blocked: 'budget_exceeded' }
  } else if (code === 'not_enabled') {
    state.status = { ...(state.status || {}), enabled: false, reason: message || '', blocked: 'ai_chat_disabled' }
  } else if (code === SETUP_CHAT_ENDED) {
    // Free setup help ran out on a plan without the assistant: the panel says so, the help buttons hide.
    const setupChat = { ...(state.status?.setup_chat || {}), available: false, ended: true }
    state.status = { ...(state.status || {}), enabled: false, reason: message || '', blocked: code, setup_chat: setupChat }
  }
}

function resetTurns(state) {
  clearTurnState(state)
  state.pollFailures = 0
  state.turnMeta = {}
  state.syncFrom = null
}

/** No conversation open: the next send starts a new one. */
function clearConversation(state) {
  state.sessionId = null
  state.sessionTitle = ''
  state.sessionPurpose = null
  state.billing = null
  state.setupRequest = null
  state.messages = []
  resetTurns(state)
  state.error = null
  state.needsSync = false
}

/**
 * The initial state with the persona re-read from localStorage: what a logout
 * or tenant switch lands on. Everything else (session, messages, sessions,
 * status, composer text, panel open) belonged to the old session/tenant.
 */
const freshInitialState = () => ({ ...initialState, persona: readPersistedPersona() })

const DRAFT_RESOLUTIONS = ['approved', 'rejected', 'expired']

/**
 * The resolution a failed confirm tells us the draft already has, or null.
 * The backend's `_get_pending_draft` 400s with code `expired` ("This draft
 * has expired...") or `resolved` ("This draft was already approved." -
 * the resolution is only in the text). Either way the draft can never be
 * saved from here, so its card must stop offering Save.
 */
function draftResolutionFromError({ code, message } = {}) {
  if (code === 'expired') return 'expired'
  const already = /already (approved|rejected|expired)/i.exec(message || '')
  if (already) return already[1].toLowerCase()
  if (code === 'resolved') return 'rejected' // resolved, but the text didn't say how: at least not saveable
  return DRAFT_RESOLUTIONS.includes(code) ? code : null
}

const lastUserSequence = (messages) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) if (messages[i].role === 'user') return messages[i].sequence
  return null
}

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    openPanel(state) { state.panelOpen = true },
    closePanel(state) { state.panelOpen = false },
    clearError(state) {
      state.error = null
      state.errorCode = null
    },
    setComposerDraft(state, action) { state.composerDraft = action.payload },
    /** A page with no chips of its own must not keep showing the last page's. */
    clearSuggestions(state) {
      state.suggestions = []
      state.suggestionsRequestId = null
    },
    /** Ignores an unrecognized value rather than storing junk from a stale caller. */
    setPersona(state, action) {
      if (!AI_PERSONA_VALUES.includes(action.payload)) return
      state.persona = action.payload
      persistPersona(action.payload)
    },
    resetConversation(state) { clearConversation(state) },
    /**
     * "Get help with this step": open the panel with `prompt` in the composer
     * (nothing is sent) and `setup` as the request's topic and step. Starts a
     * new conversation unless a setup-help one is already open.
     */
    askForSetupHelp(state, action) {
      const { prompt, setup } = action.payload
      if (state.sessionPurpose !== 'onboarding') clearConversation(state)
      state.composerDraft = prompt
      state.setupRequest = setup
      state.panelOpen = true
    },
    syncHandled(state) { state.needsSync = false },
  },
  extraReducers: (builder) => {
    builder.addCase(togglePanel, (state) => { state.panelOpen = !state.panelOpen })
    // Logout (thunk fulfilled or rejected, or the plain 401 `logout`) ends
    // the session: nothing of the old viewer's conversation may survive.
    for (const type of LOGOUT_ACTION_TYPES) builder.addCase(type, freshInitialState)
    // A tenant switch already resets this slice to `initialState` at the
    // root (store/resetOnTenantSwitch.js), which replaced the old, never-
    // dispatched `resetForTenant`; this only restores the viewer's persona
    // over that module-load snapshot.
    builder.addCase(TENANT_SWITCH_FULFILLED, freshInitialState)
    builder.addCase(TENANT_CONTEXT_CHANGED, freshInitialState)
    /** A live frame from the notifications socket. */
    builder.addCase(frameReceived, (state, action) => {
      const f = action.payload || {}
      if (!f.session_id || f.session_id !== state.sessionId) return
      // Every handler is idempotent where it can be: a page can briefly hold
      // two notification sockets (reconnects, StrictMode), and a duplicated
      // tool_started must not add a second timeline row.
      const turnId = f.turn_id || state.inFlightTurn?.id
      // Every frame carries a per-turn `seq` (backend A9), monotonic across
      // every sender of that turn -- including a confirm/reject note, which
      // joins the turn that drafted it (fix round 1) and so can arrive for
      // an EARLIER turn than the one currently in flight (confirming an
      // older draft while a new question is running). Tracked per turn id
      // (falling back to the session, matching the backend's own fallback
      // key) in a map, never a single slot: a frame for a different turn
      // must never reset another turn's high-water mark, or a redelivered
      // frame of the active turn could slip past dedup and duplicate
      // streamed text. Drop anything at or below the highest already
      // applied for THIS frame's turn. Frames from a pre-A9 backend carry no
      // `seq` at all -- always applied, same as before, so older rows still
      // render.
      if (f.seq != null) {
        const scope = f.turn_id || `session-${f.session_id}`
        const lastForScope = state.frameSeqByTurn[scope] || 0
        if (f.seq <= lastForScope) return
        state.frameSeqByTurn[scope] = f.seq
      }
      switch (f.kind) {
        case 'turn_started':
          if (state.inFlightTurn?.id === f.turn_id) {
            state.inFlightTurn.status = 'running'
            break
          }
          state.inFlightTurn = { id: f.turn_id, status: 'running', startedAt: Date.now() }
          state.turnAnchor = state.turnAnchor ?? lastUserSequence(state.messages)
          state.activities = []
          break
        case 'message_started':
          // A placeholder row holds the reply's place in the transcript, so
          // the streaming bubble and the finished one are the same element.
          upsertMessages(state, [{ id: f.message_id, sequence: f.sequence, role: 'assistant', content: '', is_error: false, tool_name: '', turn_id: turnId }])
          if (state.streaming?.messageId !== f.message_id) state.streaming = { messageId: f.message_id, text: '' }
          break
        case 'token':
          if (!state.streaming || state.streaming.messageId !== f.message_id) {
            state.streaming = { messageId: f.message_id, text: '' }
          }
          state.streaming.text += f.text || ''
          break
        case 'message_completed':
          upsertMessages(state, [{
            id: f.message_id, sequence: f.sequence, role: 'assistant', content: f.text || '',
            is_error: false, tool_name: '', turn_id: turnId,
          }])
          if (state.streaming?.messageId === f.message_id) state.streaming = null
          break
        case 'message':
          upsertMessages(state, [{
            id: f.message_id, sequence: f.sequence, role: f.role || 'assistant', content: f.text || '',
            is_error: false, ui_payload: f.ui_payload || null, tool_name: '', turn_id: turnId,
          }])
          break
        case 'tool_started':
          if (state.activities.some((a) => a.callId === f.call_id)) break
          state.activities.push({ callId: f.call_id, tool: f.tool, label: f.label || f.tool, done: false, startedAt: Date.now() })
          break
        case 'tool_finished': {
          const a = state.activities.find((x) => x.callId === f.call_id)
          if (a && !a.done) Object.assign(a, { done: true, success: f.success, summary: f.summary || '', finishedAt: Date.now() })
          break
        }
        case 'ui_payload':
          // The card's tool row, ahead of the poll that would fetch it.
          upsertMessages(state, [{ id: f.message_id, sequence: f.sequence, role: 'tool', ui_payload: f.payload, turn_id: turnId }])
          break
        case 'error':
          upsertMessages(state, [{
            id: f.message_id, sequence: f.sequence, role: 'assistant', content: f.message || 'Something went wrong.',
            is_error: true, ui_payload: null, tool_name: '', turn_id: turnId,
          }])
          state.streaming = null
          // The worker re-checks plan and budget when it picks a turn up; a
          // `code` here means the same paused/not-enabled state the status
          // endpoint would report, so the panel shows that, not just a row.
          applyBlockingErrorCode(state, f.code, f.message)
          break
        case 'session_title':
          state.sessionTitle = f.title || state.sessionTitle
          break
        case 'turn_completed':
          endTurn(state, { status: f.status })
          break
        default:
          break
      }
    })
    builder
      .addCase(loadStatus.fulfilled, (state, action) => { state.status = action.payload; state.statusError = null })
      .addCase(loadStatus.rejected, (state, action) => {
        state.status = { enabled: false }
        state.statusError = action.payload?.message || 'Assistant unavailable'
      })
      .addCase(loadStarters.fulfilled, (state, action) => { state.starters = action.payload })
      // Chips belong to one page: clear them the moment another page asks,
      // and drop any answer that isn't for the latest ask (a slow dashboard
      // response must not land on the integrations page the viewer moved to).
      .addCase(loadSuggestions.pending, (state, action) => {
        state.suggestions = []
        state.suggestionsRequestId = action.meta.requestId
      })
      .addCase(loadSuggestions.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.suggestionsRequestId) return
        state.suggestions = action.payload
      })
      .addCase(loadSuggestions.rejected, (state, action) => {
        if (action.meta.requestId !== state.suggestionsRequestId) return
        state.suggestions = []
      })
      .addCase(loadSessions.fulfilled, (state, action) => { state.sessions = action.payload })
      .addCase(startSession.pending, (state) => { state.starting = true })
      .addCase(startSession.fulfilled, (state, action) => {
        state.starting = false
        state.sessionId = action.payload.id
        state.sessionTitle = action.payload.title || ''
        state.sessionPurpose = action.payload.purpose || 'chat'
        state.billing = null
        state.messages = []
        resetTurns(state)
        state.error = null
        state.sessions = [action.payload, ...state.sessions.filter((s) => s.id !== action.payload.id)]
      })
      .addCase(startSession.rejected, (state, action) => {
        state.starting = false
        state.error = action.payload?.message || 'Could not start'
        if (action.payload?.code === SETUP_CHAT_ENDED) applyBlockingErrorCode(state, SETUP_CHAT_ENDED, action.payload.message)
      })
      .addCase(openSession.fulfilled, (state, action) => {
        state.sessionId = action.payload.session.id
        state.sessionTitle = action.payload.session.title || ''
        state.sessionPurpose = action.payload.session.purpose || 'chat'
        state.billing = null
        state.setupRequest = null
        state.messages = []
        upsertMessages(state, action.payload.messages || [])
        resetTurns(state)
        if (action.payload.inFlightTurn) {
          state.inFlightTurn = { ...action.payload.inFlightTurn, startedAt: Date.parse(action.payload.inFlightTurn.created_at) || Date.now() }
          state.turnAnchor = lastUserSequence(state.messages)
        }
        state.error = null
      })
      .addCase(openSession.rejected, (state, action) => { state.error = action.payload })
      .addCase(loadMessages.rejected, (state, action) => {
        if (action.meta?.arg?.sessionId !== state.sessionId) return
        const { message, code, status } = action.payload || {}
        if (TERMINAL_POLL_STATUSES.includes(status)) {
          const terminalCode = code || (status === 404 ? 'not_found' : 'forbidden')
          abandonTurn(state, status === 404 && !code ? POLL_NOT_FOUND_MESSAGE : message, terminalCode)
          if (terminalCode === 'ai_chat_disabled') {
            state.status = { ...(state.status || {}), enabled: false, blocked: terminalCode }
          }
          return
        }
        state.pollFailures += 1
        if (state.pollFailures >= MAX_POLL_FAILURES && state.inFlightTurn) {
          abandonTurn(state, POLL_GAVE_UP_MESSAGE, code || 'poll_failed')
        }
      })
      .addCase(loadMessages.fulfilled, (state, action) => {
        if (action.payload.sessionId !== state.sessionId) return
        state.pollFailures = 0
        const currentTurnId = state.inFlightTurn?.id ?? null
        // Fix round 1: `last_seq` is the backend's own frame high-water mark
        // for the in-flight turn as of THIS snapshot. The turn-identity
        // anchor below can't tell two same-turn polls apart (the id never
        // changed), so a response whose own last_seq trails what we've
        // already applied for that turn (from live frames, `frameSeqByTurn`)
        // is one that resolved out of order — its rows are older than what
        // is already on screen. Skip applying them rather than regress
        // already-streamed text. `last_seq` null/absent (idle turn, or a
        // pre-fix-round-1 backend) behaves as before: always applied.
        const lastSeq = action.payload.last_seq
        const appliedSeq = currentTurnId ? (state.frameSeqByTurn[currentTurnId] || 0) : 0
        const snapshotIsStale = lastSeq != null && currentTurnId != null && lastSeq < appliedSeq
        if (!snapshotIsStale) upsertMessages(state, action.payload.messages || [])
        // A stale poll must never revive a turn that already ended here (its
        // turn_completed frame beat the poll home) or replace a newer turn
        // the viewer started after the poll left. FE-A9: anchored on the
        // turn we were tracking when the request went out, not a wall-clock
        // timestamp (B5's `issuedAt`/`startedAt`, which could skew between
        // the client and whatever set `startedAt`). If that turn is no
        // longer the one we're tracking now, this response predates the
        // change and cannot say anything about current turn state, whatever
        // it claims — whether or not the message rows above were applied.
        if ((action.payload.turnIdAtDispatch ?? null) !== currentTurnId) return
        const serverTurn = action.payload.in_flight_turn || null
        if (serverTurn) {
          // Belt and suspenders for the one case turn-identity alone can't
          // catch: nothing tracked at dispatch or now (two background
          // polls), but this response describes a turn already resolved
          // here earlier in the session.
          if (state.turnMeta[serverTurn.id]) return
          if (state.inFlightTurn?.id !== serverTurn.id) {
            state.inFlightTurn = { ...serverTurn, startedAt: Date.parse(serverTurn.created_at) || Date.now() }
            state.turnAnchor = lastUserSequence(state.messages)
          }
          return
        }
        // The server says nothing is running, and we've just confirmed the
        // anchor still matches the turn we're tracking (if any).
        if (state.inFlightTurn) endTurn(state)
      })
      .addCase(sendMessage.pending, (state, action) => {
        state.sending = true
        state.error = null
        state.errorCode = null
        state.pollFailures = 0
        // A reply sent from a transcript card (`keepDraft`) leaves whatever
        // the viewer is drafting in the composer alone.
        if (!action.meta?.arg?.keepDraft) state.composerDraft = ''
        state.pendingUserText = action.meta?.arg?.text || ''
        state.pendingStartedAt = Date.now()
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sending = false
        state.billing = action.payload.billing || null
        state.setupRequest = null
        upsertMessages(state, [action.payload.message])
        if (action.payload.message?.id && state.pendingStartedAt) state.exchangeKeys[action.payload.message.id] = `p-${state.pendingStartedAt}`
        state.inFlightTurn = { ...action.payload.turn, startedAt: state.pendingStartedAt ?? Date.now() }
        state.pendingUserText = ''
        state.pendingStartedAt = null
        state.turnAnchor = action.payload.message?.sequence ?? lastUserSequence(state.messages)
        state.activities = []
        state.stoppingTurnId = null
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sending = false
        state.pendingUserText = ''
        state.pendingStartedAt = null
        state.error = action.payload?.message || 'Could not send'
        // sendMessage.pending cleared composerDraft optimistically; a
        // failed send (network error, budget/plan block, etc.) puts the
        // text back rather than silently discarding what the viewer typed
        // (fix round 2, B4). The thunk's own argument, not state, is the
        // source of truth for what was actually sent.
        // A card send never touched the draft, so there is nothing to restore.
        if (action.meta?.arg?.text && !action.meta.arg.keepDraft) state.composerDraft = action.meta.arg.text
        if (action.payload?.code === 'budget_exceeded' || action.payload?.code === 'ai_chat_disabled') {
          state.status = { ...(state.status || {}), enabled: action.payload.code !== 'ai_chat_disabled', blocked: action.payload.code }
        }
        if (action.payload?.code === SETUP_CHAT_ENDED) applyBlockingErrorCode(state, SETUP_CHAT_ENDED, action.payload.message)
      })
      .addCase(cancelTurn.pending, (state, action) => {
        state.cancelling = true
        state.stoppingTurnId = action.meta?.arg ?? state.inFlightTurn?.id ?? null
      })
      .addCase(cancelTurn.fulfilled, (state, action) => {
        state.cancelling = false
        // Usually `cancel_requested`: the worker stops at its next check and
        // the turn ends through a frame or a poll, marked as stopped.
        if (action.payload?.turn?.status === 'cancelled') endTurn(state, { status: 'cancelled' })
      })
      .addCase(cancelTurn.rejected, (state, action) => {
        state.cancelling = false
        state.stoppingTurnId = null
        state.error = action.payload || 'Could not stop the reply'
      })
      .addCase(confirmDraft.pending, (state, action) => {
        state.draftBusy[action.meta.arg] = true
        delete state.draftErrors[action.meta.arg]
      })
      .addCase(confirmDraft.fulfilled, (state, action) => {
        const { draftId, kind, id, name, url_path, report_id, status, setup_run_id, connection_id } = action.payload
        delete state.draftBusy[draftId]
        state.resolvedDrafts[draftId] = 'approved'
        // The applier returns `kind` for the object kind (metric, dashboard,
        // report_schedule, report_template, …); a pre-B2 backend instead
        // returns a bare `report_id` with no `kind` at all, so that shape
        // still resolves to a `report` link.
        const objectKind = kind || (report_id != null ? 'report' : undefined)
        const objectId = id ?? report_id
        if (objectKind || objectId != null || name || url_path) {
          state.savedDrafts[draftId] = { kind: objectKind, id: objectId, name, url_path }
        }
        if (status === 'accepted' && setup_run_id) {
          state.savedDrafts[draftId] = { ...state.savedDrafts[draftId], status, setup_run_id, connection_id }
        }
        state.needsSync = true
      })
      .addCase(confirmDraft.rejected, (state, action) => {
        const draftId = action.payload?.draftId
        delete state.draftBusy[draftId]
        const resolution = draftResolutionFromError(action.payload)
        if (draftId && resolution) {
          // The card now says why (Saved / Not saved / expired); no banner needed.
          state.resolvedDrafts[draftId] = resolution
          return
        }
        const code = action.payload?.code
        if (draftId && CARD_CONFIRM_ERROR_CODES.includes(code)) {
          state.draftErrors[draftId] = { code, message: action.payload?.message || '', meta: action.payload?.meta || null }
          if (code === 'rock_writes_disabled' && state.status) {
            state.status.rock_writes = { ...(state.status.rock_writes || {}), enabled: false }
          }
          return
        }
        state.error = action.payload?.message || 'Could not save'
      })
      .addCase(rejectDraft.pending, (state, action) => { state.draftBusy[action.meta.arg.draftId] = true })
      .addCase(rejectDraft.fulfilled, (state, action) => {
        delete state.draftBusy[action.payload.draftId]
        state.resolvedDrafts[action.payload.draftId] = 'rejected'
        state.needsSync = true
      })
      .addCase(rejectDraft.rejected, (state, action) => {
        delete state.draftBusy[action.payload?.draftId]
        state.error = action.payload?.message || 'Could not dismiss'
      })
      .addCase(sendFeedback.fulfilled, (state, action) => {
        state.feedback[action.payload.messageId] = action.payload.is_positive
      })
  },
})

export const {
  openPanel, closePanel, clearError, resetConversation, syncHandled, setPersona, setComposerDraft, clearSuggestions,
  askForSetupHelp,
} = aiChatSlice.actions
export { frameReceived, togglePanel }

export const selectAiChat = (state) => state.aiChat

export const selectLastSequence = (state) => {
  const msgs = state.aiChat.messages
  return msgs.length ? msgs[msgs.length - 1].sequence : 0
}
/** Where a poll starts: the in-flight turn's anchor, so rows filled in after they were first seen are re-read. */
export const selectPollAfter = (state) => state.aiChat.turnAnchor ?? selectLastSequence(state)

export default aiChatSlice.reducer
